import React, { useEffect } from 'react';
import bemBlock from '@gik/core/utils/bemBlock';
import { Grid, ArticleTile } from '@gik/ui';
import { usePostsFeatured } from '@gik/blog/api/wp';
import WordpressPost from '@gik/core/models/wordpress/WordpressPost';
import WordpressConverter from '@gik/blog/utils/WordpressConverter';
import getPreviewMode from '@gik/core/utils/PreviewMode';
import Article from '@gik/blog/models/Article';
import withSuspense from '@gik/core/components/suspended/withSuspense';

export interface IInkindUsesGridProps {
  cols: number;
  perPage: number;
  skeleton?: boolean;
  skeletonItems?: number;
  showCategory?: boolean;
  showDescription?: boolean;
  showAuthor?: boolean;
}

const skelBem = bemBlock(`inkind-uses-grid-skeleton`);
const Skeleton = ({
  skeletonItems = 4,
  showCategory,
  showDescription = true,
  showAuthor,
  cols,
}: Partial<IInkindUsesGridProps>): React.ReactElement => (
  <Grid cols={cols} className={`${skelBem()}`}>
    {Array.from(Array(skeletonItems).keys()).map(index => {
      return (
        <ArticleTile
          skeleton
          key={index}
          showCategory={showCategory}
          showDescription={showDescription}
          showAuthor={showAuthor}
        />
      );
    })}
  </Grid>
);

function convertPoststoArticleLinks(posts: WordpressPost[]) {
  const previewMode = getPreviewMode();
  return posts
    .map(post => WordpressConverter.convertPost(post))
    .filter(post => !post.isPreviewOnly || post.isPreviewOnly === previewMode);
}

function InkindUsesGrid({
  cols,
  showCategory,
  showDescription = true,
  showAuthor,
  perPage,
}: IInkindUsesGridProps): React.ReactElement {
  const bem = bemBlock('inkind-uses-grid');

  const { data: posts, error: postsError } = usePostsFeatured('benefits-others', perPage);

  const [articles, setArticles] = React.useState<Article[]>();
  useEffect(() => {
    if (!posts) return;
    setArticles(convertPoststoArticleLinks(posts));
  }, [posts]);

  const isLoading = !posts;
  const isError = isLoading && postsError;

  if (isLoading) return <Skeleton />;
  if (isError) return <Skeleton />;

  return (
    <Grid cols={cols} className={bem()}>
      {articles?.map((article, index) => (
        <ArticleTile
          key={index}
          article={article}
          showCategory={showCategory}
          showDescription={showDescription}
          showAuthor={showAuthor}
        />
      ))}
    </Grid>
  );
}

export default withSuspense(InkindUsesGrid, Skeleton);
